import React from "react";
import styles from "./PasswordField.scss";
import cx from "../../utils/cx";
import TextField, { ITextFieldProps } from "./TextField";
import getUid from "../../utils/getUid";
import { Accordion, AccordionDetails, AccordionSummary, FormControl, IconButton, InputAdornment, InputLabel, InputProps, OutlinedInput } from "@mui/material";
import { Visibility, VisibilityOff, VisibilityOffOutlined, VisibilityOutlined, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
// import { ExpandRecursively } from "../../interfaces/TypeHelper";

export type IPasswordFieldProps =  {
    className?: string;
    label?: string;
    name: string;
    value: string;
    onChange: (value: string | null) => void; // value = null if not valid or repeat password does not match
// } & Omit<ITextFieldProps, 'onChange' | 'variant'>;
} & Omit<InputProps, 'onChange' | 'variant'>;


// type ExpandedType = ExpandRecursively<IPasswordFieldProps>

export const passwordCheck = (password: string, repeatedPassword) => {
    const length = password.length;
    const includesLowerCaseLetters = password.match(/[a-z]/) ? 1 : 0;
    const includesUpperCaseLetters = password.match(/[A-Z]/) ? 1 : 0;
    const includesNumbers = password.match(/[0-9]/) ? 1 : 0;
    const includesSpecialCharacters = password.match(/[^a-zA-Z0-9]/) ? 1 : 0;

    const includesTypes = includesLowerCaseLetters + includesUpperCaseLetters + includesNumbers + includesSpecialCharacters;

    let complexity = Math.min(100,
        length * 2.5
        + (includesLowerCaseLetters ? length * 1.5 : 0)
        + (includesUpperCaseLetters ? length * 1.5 : 0)
        + (includesNumbers ? length : 0)
        + (includesSpecialCharacters ? length : 0)
    );

    // color analog to Drupal password strength
    const color = (length >= 8 && includesTypes >= 4) ? 'green'
        : (length >= 25 && includesTypes >= 2) ? 'green'
                : complexity < 60 ? 'red'
                    : complexity < 70 ? 'yellow' : 'yellow';
    if (color !== 'green') {
        complexity = Math.min(95, complexity);
    }

    const isIdentical = password === repeatedPassword;

    return {
        length,
        complexity,
        color,
        includesLowerCaseLetters,
        includesUpperCaseLetters,
        includesNumbers,
        includesSpecialCharacters,
        isIdentical,
        isValid: color === 'green' && isIdentical,
    }

};

const PasswordField: React.FC<IPasswordFieldProps> = ({ className = '', name, value, onChange, label, ...restTextfieldProps }) => {
    const [labelUid] = React.useState(() => getUid('formId'));

    const [currentValue, setCurrentValue] = React.useState(value);
    const [repeatValue, setRepeatValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);


    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const currentValue = ev.target.value;
        setCurrentValue(currentValue);
        checkSubmit(currentValue, repeatValue);
    };

    const handleChangeRepeat = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const repeatValue = ev.target.value;
        setRepeatValue(repeatValue);
        checkSubmit(currentValue, repeatValue);
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const checkSubmit = (currentValue, repeatValue) => {
        const { isValid} = passwordCheck(currentValue, repeatValue);
        if (isValid) {
            onChange(currentValue);
        } else {
            onChange(null);

        }
    };

    const handleBlur = () => {
        checkSubmit(currentValue, repeatValue);
    };


    const { length, complexity, color, includesLowerCaseLetters, includesUpperCaseLetters, includesNumbers, includesSpecialCharacters, isIdentical } = passwordCheck(currentValue, repeatValue);

    const isEmpty = length === 0;

    return <div className={cx(styles.passwordField, className)}>
        {/* <TextField
            {...restTextfieldProps}
            label={label || "Passwort"}
            className={'password-field js-password-field form-text required form-item__textfield'}
            // data-drupal-selector="edit-pass-pass1"
            type="password"
            id={`${labelUid}Pass`}
            name={name}
            value={currentValue}
            inputProps={{ ...restTextfieldProps.inputProps, maxLength: 512 }}
            required={true}
            aria-required="true"
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete="
            new-password"
        /> */}

        <MuiPasswordField 
            {...restTextfieldProps}
            label={label || "Passwort"}
            className={'password-field js-password-field form-text required form-item__textfield'}
            // data-drupal-selector="edit-pass-pass1"
            type="password"
            id={`${labelUid}Pass`}
            name={name}
            value={currentValue}
            inputProps={{ ...restTextfieldProps.inputProps, maxLength: 512 }}
            required={true}
            aria-required="true"
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete="new-password"
        />
        

        <div className={cx(styles.complexityIndicator, styles[color])}>
            <div className={styles.bar}>
                <span style={{ width: complexity + '%' }} className={styles.indicator}></span>
                <span style={{ width: (100 - complexity) + '%' }}></span>
            </div>
            <div className={styles.word}>{isEmpty ? '' : color === 'green' ? 'sehr stark' : color === 'yellow' ? 'stark' : 'schwach' }</div>
        </div>

        {(isFocused) && <ul className={styles.passwordHints}>
            <li className={isEmpty ? styles.empty : length >= 8 ? styles.valid : styles.invalid}>
                {length >= 8 ? 'Mindestens 8 Zeichen' : 'Mindestens 8 Zeichen (noch ' + (8 - length) + ' Zeichen)'}
            </li>
            {/* <li className={isEmpty ? styles.empty : includesLowerCaseLetters ? styles.valid : color === 'green' ? styles.inactive : styles.invalid}>
                {includesLowerCaseLetters ? 'Kleinbuchstaben' : 'Kleinbuchstaben (noch 1 Zeichen)'}
            </li>
            <li className={isEmpty ? styles.empty : includesUpperCaseLetters ? styles.valid : color === 'green' ? styles.inactive : styles.invalid}>
                {includesUpperCaseLetters ? 'Großbuchstaben' : 'Großbuchstaben (noch 1 Zeichen)'}
            </li> */}
            <li className={isEmpty ? styles.empty : includesUpperCaseLetters && includesLowerCaseLetters ? styles.valid : color === 'green' ? styles.inactive : styles.invalid}>
                {/* {includesUpperCaseLetters && includesLowerCaseLetters ? 'Groß- und Kleinbuchstaben' : `Groß- und Kleinbuchstaben (noch ${includesUpperCaseLetters ? 1 : 2} Zeichen)`} */}
                Groß- und Kleinbuchstaben
            </li>
            <li className={isEmpty ? styles.empty : includesNumbers ? styles.valid : color === 'green' ? styles.inactive : styles.invalid}>
                {/* {includesNumbers ? 'Zahlen' : 'Zahlen (noch 1 Zeichen)'} */}
                Zahlen 
            </li>
            <li className={isEmpty ? styles.empty : includesSpecialCharacters ? styles.valid : color === 'green' ? styles.inactive : styles.invalid}>
                {/* {includesSpecialCharacters ? 'Sonderzeichen, wie z.B. !?&%' : 'Sonderzeichen (noch 1 Zeichen)'} */}
                Sonderzeichen, wie z.B. !?&%
            </li>
        </ul>}

        {(isFocused) &&  <PasswordInfoText />}

        <MuiPasswordField
            label="Passwort wiederholen"
            // className={'password-field js-password-field form-text required form-item__textfield'}
            type="password"
            id={`${labelUid}PassRepeat`}
            name={`${name}Repeat`}
            inputProps={{ maxLength: 512 }}
            required={true}
            aria-required="true"
            value={repeatValue}
            onChange={handleChangeRepeat}
            autoComplete="new-password"
        />
        <div className={styles.match}>
            {repeatValue
                ? <span>Passwörter stimmen überein: {isIdentical ? 'Ja' : <span className={styles.invalid}>Nein</span>}</span>
                : <span>&nbsp;</span>}
        </div>

    </div>
};

type IMuiPasswordFieldProps = {
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;

} & Omit<IPasswordFieldProps, 'onChange'>;

const MuiPasswordField: React.FC<IMuiPasswordFieldProps> = ({ className = '', name, value, onChange, label, ...restTextfieldProps }) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        onChange(ev);
    };


    return <FormControl variant="outlined" fullWidth className={className}>
        <InputLabel htmlFor="outlined-adornment-password">{label}</InputLabel>
        <OutlinedInput
            {...restTextfieldProps}
            fullWidth
            id="outlined-adornment-password"
            type={showPassword ? 'text' : 'password'}
            onChange={handleChange}
            name={name}
            value={value}
            endAdornment={
                <InputAdornment position="end">
                    <IconButton
                        aria-label="Passwort Sichtbarkeit Umschalter"
                        aria-pressed={ showPassword ? true : false }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                    >
                        {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                    </IconButton>
                </InputAdornment>
            }
            label={label}
        />
    </FormControl>
}

const PasswordInfoText = () => {

    // const [isOpen, setIsOpen] = React.useState(false);

    return <div className={cx(styles.passwordField, styles.passwordInfoText)}>
        {/* <div onClick={()=>setIsOpen(!isOpen)}>Empfehlungen zu sicheren Passwörtern</div> */}

        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
            >Empfehlungen zu sicheren Passwörtern</AccordionSummary>
 
            <AccordionDetails>
                <ul>
                    <li>
                        Verwende bei kurzen Passwörtern (8-12 Zeichen) vier verschiedene Zeichenarten (Klein- & Großbuchstaben, Sonderzeichen und Zahlen), i
                        dealerweise in willkürlicher Reihenfolge.
                    </li>
                    <li>
                        Verwende bei langen Passwörtern (ab 25 Zeichen) mindestens zwei verschiedene Zeichenarten 
                        (z. B. Klein- & Großbuchstaben und/oder Sonderzeichen) - hier bieten sich z. B. sechs aneinandergereihte Wörter an, 
                        die durch Zeichen getrennt sind.
                    </li>
                    <li>
                        Meide Namen von Familienmitgliedern, Haustieren und Geburtsdaten.
                    </li>
                    <li>
                        Meide die Verwendung von Passwörtern, die du bereits benutzt.
                    </li>
                    <li>
                        Meide einfache oder bekannte Wiederholungs- und Tastaturmuster wie z. B. “1234abcd” oder “asdfgh”.
                    </li>
                    <li>
                        Das vollständige Passwort sollte nicht im Wörterbuch vorkommen.
                    </li>
                    <li>
                        Speichere dir das Passwort in einem sicheren Passwortmanager und teile es nicht mit anderen!
                    </li>
                </ul>
            </AccordionDetails>
        </Accordion>
        
    </div>
}

export default PasswordField;
